import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { PrimaryCTA } from './CTAButton';
import { trackEvent } from '../../lib/track';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sound-showcase-4/artifacts/9p7oxb75_Audio_%26_Co_Logo_Audio_and_Co_Logo_v2_Black.png';

// Topic-cluster hub pages. Kept as static list (matches pillars.data + the
// two top-level hubs) so the header renders without needing an API call.
const GUIDES = [
  { to: '/podcast-launch',                 label: 'How to Launch a Podcast',   blurb: 'Kit, format, first 5 episodes, launch costs.' },
  { to: '/podcast-management',             label: 'Podcast Management',        blurb: 'Editing, publishing, show notes, distribution.' },
  { to: '/book-publishing',                label: 'Book Publishing',           blurb: 'Self-publish vs traditional vs done-for-you.' },
  { to: '/guides/podcast-production-uk',   label: 'Podcast Production 2026',   blurb: 'The 2026 done-for-you production playbook.' },
  { to: '/guides/authority-hub-uk',        label: 'Business Websites',        blurb: 'Podcast, books, YouTube, blog + services on one site.' },
  { to: '/guides/content-repurposing-uk',  label: 'Content Repurposing',       blurb: 'One recording, 12 outputs, every week.' },
  { to: '/guides/small-business-seo-uk',   label: 'Small Business SEO',        blurb: 'A plain-English 2026 SEO playbook.' },
];

// Service dropdowns added Sep 2026 per rebuild spec. Each dropdown item
// points to a top-level service page (not a blog post) so it's an on-ramp
// into a full landing page.
const PODCAST_SERVICES = [
  { to: '/podcast-production',        label: 'Podcast Production',    blurb: 'You record. We edit, mix, master, publish. From £120/ep.' },
  { to: '/podcast-editing',           label: 'Podcast Editing',       blurb: 'Editing only, per episode, no lock-in. From £90/ep.' },
  { to: '/podcast-launch',            label: 'Podcast Launch',        blurb: 'Strategy, artwork, first 5 episodes. Launch in under 30 days.' },
  { to: '/podcast-visibility-package', label: 'Visibility Package',   blurb: 'Best for most clients. Full weekly engine, £1,213/mo.' },
  { to: '/podcasts',                  label: 'Client Shows',          blurb: 'A hand-picked selection of client shows we produce and support.' },
];

const YOUTUBE_SERVICES = [
  { to: '/podcast-to-youtube', label: 'Podcast to YouTube', blurb: 'One episode becomes 1 YouTube video + 21 daily Shorts.' },
];

const AUTHOR_SERVICES = [
  { to: '/audiobook-production', label: 'Audiobook Production', blurb: 'Editing, mastering, ACX submission. £60/hour.' },
  { to: '/self-publishing',      label: 'Self-Publishing',      blurb: 'Amazon KDP done for you. Live in 4-6 weeks.' },
];

// Growth = the non-podcast services that keep leads flowing. Kept as its
// own dropdown so SEO and Website Builds get proper nav prominence.
const GROWTH_SERVICES = [
  { to: '/seo',                        label: 'SEO Service',         blurb: 'Technical + content SEO. From £480/month.' },
  { to: '/websites-apps',              label: 'Website & App Builds', blurb: 'Sites and apps that convert. Project-based.' },
  { to: '/podcast-visibility-package', label: 'Visibility Package',   blurb: 'Best for most clients. Full weekly engine.' },
];

const NAV = [
  { type: 'dropdown', label: 'Podcast', items: PODCAST_SERVICES },
  { type: 'dropdown', label: 'YouTube', items: YOUTUBE_SERVICES },
  { type: 'dropdown', label: 'Authors', items: AUTHOR_SERVICES },
  { type: 'dropdown', label: 'Growth',  items: GROWTH_SERVICES },
  { to: '/build-your-package', label: 'Your Package', highlight: true },
  { to: '/authority-hub',  label: 'Business Website' },
  { to: '/results',        label: 'Results' },
  { to: '/about',          label: 'About' },
  { type: 'dropdown',      label: 'Guides', items: GUIDES },
  { to: '/blog',           label: 'Blog' },
];

function NavDropdown({ label, items }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();
  const isActive = items.some((g) => pathname === g.to);

  useEffect(() => { setOpen(false); }, [pathname]);

  const onEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const onLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  const testKey = label.toLowerCase().replace(/\W+/g, '-');

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid={`v2-nav-${testKey}-wrap`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 text-[15px] font-medium transition ${
          isActive || open ? 'text-[#1E2A44]' : 'text-[#64748B] hover:text-[#1E2A44]'
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`v2-nav-${testKey}`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[360px] bg-white border border-[#E2E8F0] rounded-[14px] shadow-[0_20px_60px_rgba(30,42,68,0.12)] p-2 z-50"
          data-testid={`v2-nav-${testKey}-menu`}
        >
          {items.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              role="menuitem"
              className="block px-4 py-3 rounded-[10px] hover:bg-[#F6F8FB] transition"
              data-testid={`v2-nav-${testKey}-item-${g.to.replace(/\W+/g, '-')}`}
            >
              <div className="font-display font-semibold text-[#1E2A44] text-[15px] leading-snug">{g.label}</div>
              <div className="text-[#64748B] text-[13px] leading-snug mt-0.5">{g.blurb}</div>
            </Link>
          ))}
          {label === 'Guides' && (
            <div className="mt-1 pt-2 border-t border-[#E2E8F0]">
              <Link
                to="/guides"
                role="menuitem"
                className="flex items-center justify-between px-4 py-2.5 rounded-[10px] hover:bg-[#F6F8FB] transition"
                data-testid="v2-nav-guides-item-all"
              >
                <span className="font-display font-semibold text-[#BC516F] text-[14px]">See all guides</span>
                <span className="text-[#BC516F] text-[14px]">→</span>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function V2Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpenGroups, setMobileOpenGroups] = useState({});
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); setMobileOpenGroups({}); }, [pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all bg-white/95 backdrop-blur ${scrolled ? 'shadow-[0_4px_20px_rgba(30,42,68,0.06)]' : ''} border-b border-[#E2E8F0]`}
      data-testid="v2-header"
    >
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6 lg:px-8 h-16 lg:h-[72px] flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="v2-logo" aria-label="Audio & Co, home">
          <img
            src={LOGO_URL}
            alt="Audio & Co.® logo, UK podcast management, book publishing and websites built for SEO"
              title="Audio & Co. | Podcast Management & SEO Websites"
            className="h-9 sm:h-10 w-auto"
            width="120" height="40"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV.map((n) => {
            if (n.type === 'dropdown') {
              return <NavDropdown key={`dd-${n.label}`} label={n.label} items={n.items} />;
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-[15px] font-medium transition ${
                    isActive ? 'text-[#1E2A44]' : 'text-[#64748B] hover:text-[#1E2A44]'
                  }`
                }
                data-testid={`v2-nav-${n.to.replace('/', '')}`}
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block" onClick={() => trackEvent('header_cta_click', 'desktop')}>
            <PrimaryCTA size="sm" data-testid="v2-header-cta">Check if we&apos;re a fit</PrimaryCTA>
          </div>
          <div className="sm:hidden" onClick={() => trackEvent('header_cta_click', 'mobile')}>
            <PrimaryCTA size="sm" className="!px-3 !py-1.5 !text-[13px]" data-testid="v2-header-cta-mobile">Check fit</PrimaryCTA>
          </div>
          <button
            className="lg:hidden p-2 -mr-2 text-[#1E2A44]"
            onClick={() => setOpen(o => !o)}
            aria-label="Toggle menu"
            data-testid="v2-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white max-h-[calc(100vh-64px)] overflow-y-auto" data-testid="v2-mobile-menu">
          <nav className="px-5 py-3 space-y-1">
            {NAV.map((n) => {
              if (n.type === 'dropdown') {
                const isOpen = !!mobileOpenGroups[n.label];
                const key = n.label.toLowerCase().replace(/\W+/g, '-');
                return (
                  <div key={`mobile-${key}`}>
                    <button
                      type="button"
                      onClick={() => setMobileOpenGroups((g) => ({ ...g, [n.label]: !g[n.label] }))}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-base font-medium text-[#475569] hover:bg-[#F6F8FB] transition"
                      data-testid={`v2-mobile-nav-${key}`}
                    >
                      {n.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="pl-3 space-y-1 pb-1">
                        {n.items.map((g) => (
                          <NavLink
                            key={g.to}
                            to={g.to}
                            className={({ isActive }) =>
                              `block px-3 py-2 rounded-lg text-[14px] font-medium transition ${
                                isActive ? 'text-[#1E2A44] bg-[#F6F8FB]' : 'text-[#475569] hover:bg-[#F6F8FB]'
                              }`
                            }
                            data-testid={`v2-mobile-nav-${key}-item-${g.to.replace(/\W+/g, '-')}`}
                          >
                            {g.label}
                          </NavLink>
                        ))}
                        <NavLink
                          to="/guides"
                          className="block px-3 py-2 rounded-lg text-[14px] font-semibold text-[#BC516F] hover:bg-[#F6F8FB] transition"
                          data-testid="v2-mobile-nav-guides-item-all"
                        >
                          See all guides →
                        </NavLink>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `block px-3 py-3 rounded-lg text-base font-medium transition ${
                      isActive ? 'text-[#1E2A44] bg-[#F6F8FB]' : 'text-[#475569] hover:bg-[#F6F8FB]'
                    }`
                  }
                  data-testid={`v2-mobile-nav-${n.to.replace('/', '')}`}
                >
                  {n.label}
                </NavLink>
              );
            })}
            <div className="pt-3">
              <PrimaryCTA size="md" className="w-full" data-testid="v2-mobile-cta">Check if we&apos;re a fit</PrimaryCTA>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
